import React, { useEffect, useState } from "react";
import {
  Form,
  Input,
  Button,
  Card,
  Row,
  Col,
  Select,
  Switch,
  Tooltip,
  Upload,
  message,
  Spin,
  Modal,
  Space,
  Typography,
} from "antd";
import {
  InfoCircleOutlined,
  UploadOutlined,
  StopOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
} from "@ant-design/icons";
import EndpointFunctions from "../../../utils/Functions/endpointFunctions";
import Endpoints from "../../../api/endpoints";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";

const ShopInformation = ({
  initialValues,
  isLoading,
  sectors,
  isSectorLoading,
  shopId,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const axiosPrivate = useAxiosPrivate();
  const [imageFileList, setImageFileList] = useState([]);
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [displayInDiscoveryList, setDisplayInDiscoveryList] = useState(false);
  const [isDiscoveryToggleLoading, setIsDiscoveryToggleLoading] =
    useState(false);
  const [shopStatus, setShopStatus] = useState(1);
  const [isStatusToggleLoading, setIsStatusToggleLoading] = useState(false);
  const [shopLocation, setShopLocation] = useState(null);
  const [isClearLocationLoading, setIsClearLocationLoading] = useState(false);

  console.log("initialValues", initialValues);

  useEffect(() => {
    if (initialValues) {
      const formValues = {
        name: initialValues.name || "",
        email: initialValues.email || "",
        sector: { id: initialValues.sector?.id },
        webSiteUrl: initialValues.webSiteUrl || "",
        instagramAccount: initialValues.instagramAccount || "",
        facebookAccount: initialValues.facebookAccount || "",
        xaccount: initialValues.xaccount || "",
        address: initialValues.address || "",
      };
      form.setFieldsValue(formValues);
      setDisplayInDiscoveryList(Boolean(initialValues.displayInDiscoveryList));
      if (typeof initialValues.status === "number") {
        setShopStatus(initialValues.status);
      }
      setShopLocation(initialValues.location || null);
    }
  }, [initialValues, form]);

  const isSuspended = shopStatus === 0;

  const handleSuspendToggle = () => {
    const willSuspend = !isSuspended;
    Modal.confirm({
      title: willSuspend ? "Üyeliği Askıya Al" : "Üyeliği Aktifleştir",
      content: willSuspend
        ? "Bu işletmeyi askıya almak istediğinize emin misiniz? İşletme sahibi uygulamaya giriş yapamayacak. Müşterilerin mevcut kartları DB'de korunur. Bu işlem geri alınabilir."
        : "Bu işletmeyi tekrar aktifleştirmek istediğinize emin misiniz? İşletme sahibi tekrar giriş yapabilecek.",
      okText: willSuspend ? "Askıya Al" : "Aktifleştir",
      cancelText: "Vazgeç",
      okButtonProps: { danger: willSuspend },
      onOk: async () => {
        setIsStatusToggleLoading(true);
        const response = await EndpointFunctions.putData({
          endpointURL: willSuspend
            ? Endpoints.suspendShop
            : Endpoints.activateShop,
          axiosPrivate: axiosPrivate,
          params: { shopId: shopId },
        });
        if (response.success) {
          setShopStatus(willSuspend ? 0 : 1);
          message.success(
            willSuspend
              ? "İşletme askıya alındı"
              : "İşletme tekrar aktifleştirildi"
          );
        } else {
          message.error(response.errorMessage || "İşlem başarısız");
        }
        setIsStatusToggleLoading(false);
      },
    });
  };

  const hasLocation = Boolean(
    shopLocation &&
      (shopLocation.latitude != null ||
        shopLocation.longitude != null ||
        shopLocation.googlePlaceId ||
        shopLocation.formattedAddress)
  );

  const handleClearLocation = () => {
    Modal.confirm({
      title: "Harita konumunu kaldır",
      content:
        "Bu işletmenin harita konumu silinecek ve müşteri keşif haritasından düşecek. İşletme sahibi isterse kendi uygulamasından yeniden konum atayabilir. Bu işlem geri alınabilir.",
      okText: "Konumu Kaldır",
      cancelText: "Vazgeç",
      okButtonProps: { danger: true },
      onOk: async () => {
        setIsClearLocationLoading(true);
        const response = await EndpointFunctions.deleteData({
          endpointURL: Endpoints.clearShopLocation,
          axiosPrivate: axiosPrivate,
          requestBody: { params: { shopId: shopId } },
        });
        if (response.success) {
          setShopLocation(null);
          message.success("Harita konumu kaldırıldı");
        } else {
          message.error(response.errorMessage || "Konum kaldırılamadı");
        }
        setIsClearLocationLoading(false);
      },
    });
  };

  const handleDiscoveryToggle = async (newValue) => {
    const previousValue = displayInDiscoveryList;
    setDisplayInDiscoveryList(newValue);
    setIsDiscoveryToggleLoading(true);
    const response = await EndpointFunctions.putData({
      endpointURL: Endpoints.setShopDisplayInDiscoveryList,
      axiosPrivate: axiosPrivate,
      params: { shopId: shopId, value: newValue },
    });
    if (response.success) {
      message.success("Keşif listesi tercihi güncellendi");
    } else {
      setDisplayInDiscoveryList(previousValue);
      message.error("Keşif listesi tercihi güncellenemedi");
    }
    setIsDiscoveryToggleLoading(false);
  };

  const handleSubmit = async () => {
    const values = await form.validateFields();
    setIsSubmitLoading(true);
    const requestBody = {
      shopId: shopId,
      name: values.name,
      email: values.email,
      sectorId: values.sector.id,
      webSiteUrl: values.webSiteUrl,
      instagramAccount: values.instagramAccount,
      facebookAccount: values.facebookAccount,
      xaccount: values.xaccount,
      address: values.address,
    };

    let formData = new FormData();
    const json = JSON.stringify(requestBody);
    const blob = new Blob([json], {
      type: "application/json",
    });
    formData.append("request", blob);

    // Get file from imageFileList instead of form values
    const imageFile = imageFileList[0]?.originFileObj;
    if (imageFile) {
      formData.append("image", imageFile);
    }

    const response = await EndpointFunctions.putData({
      endpointURL: Endpoints.editShop,
      axiosPrivate: axiosPrivate,
      formData: formData,
      isMultiPart: true,
    });
    console.log(response);
    if (response.success) {
      message.success("Mağaza bilgileri güncellendi");
      setImageFileList([]);
      if (onSuccess) {
        onSuccess();
      }
    } else {
      message.error(response.errorMessage || "Mağaza bilgileri güncellenemedi");
      console.error("updateShopDetails error:", response);
    }
    setIsSubmitLoading(false);
  };

  return (
    <Spin spinning={isSubmitLoading}>
      <Card
        type="inner"
        title={
          <Space>
            <span>Mağaza Bilgileri</span>
            {isSuspended && (
              <span style={{ color: "#ff4d4f", fontWeight: "normal" }}>
                (Askıya Alındı)
              </span>
            )}
          </Space>
        }
        extra={
          <Space>
            <Button
              danger={!isSuspended}
              type={isSuspended ? "default" : "default"}
              icon={isSuspended ? <CheckCircleOutlined /> : <StopOutlined />}
              onClick={handleSuspendToggle}
              loading={isStatusToggleLoading}
              style={
                isSuspended
                  ? { borderColor: "#52c41a", color: "#52c41a" }
                  : undefined
              }>
              {isSuspended ? "Üyeliği Aktifleştir" : "Üyeliği Askıya Al"}
            </Button>
            <Button type="primary" onClick={handleSubmit} loading={isLoading}>
              Kaydet
            </Button>
          </Space>
        }>
        <Form form={form} layout="vertical">
          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Form.Item name="name" label="Mağaza Adı">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="email" label="E-posta">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name={["sector", "id"]} label="Sektör">
                <Select options={sectors} loading={isSectorLoading} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label={
                  <span>
                    Keşif Listesinde Göster{" "}
                    <Tooltip title="Konumu olmayan üye işletmeler tab'ında müşterilere görünür olsun mu? 'asdasd' tarzı dummy/test kayıtları için kapalı tutun. Değişiklik anında uygulanır, Kaydet'i beklemez.">
                      <InfoCircleOutlined style={{ color: "#999" }} />
                    </Tooltip>
                  </span>
                }>
                <Switch
                  checked={displayInDiscoveryList}
                  loading={isDiscoveryToggleLoading}
                  onChange={handleDiscoveryToggle}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="webSiteUrl" label="Web Sitesi">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="instagramAccount" label="Instagram">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="facebookAccount" label="Facebook">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="xaccount" label="X (Twitter)">
                <Input />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="address" label="Adres">
                <Input.TextArea />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={
                  <span>
                    Masa QR Linki{" "}
                    <Tooltip title="İşletmenin web katılım (app'siz kayıt) linki. Müşteri bu URL'yi telefonuyla açıp kayıt olur. İşletme 'Masa QR Kodum' ekranını/posterini hiç açmadıysa link henüz üretilmemiş olabilir.">
                      <InfoCircleOutlined style={{ color: "#999" }} />
                    </Tooltip>
                  </span>
                }>
                {initialValues?.webCode ? (
                  <Typography.Text
                    copyable={{
                      text: `https://web.mahalle.app/s/${initialValues.webCode}`,
                    }}
                    style={{ fontFamily: "monospace", fontSize: 13 }}>
                    <a
                      href={`https://web.mahalle.app/s/${initialValues.webCode}`}
                      target="_blank"
                      rel="noopener noreferrer">
                      {`https://web.mahalle.app/s/${initialValues.webCode}`}
                    </a>
                  </Typography.Text>
                ) : (
                  <span style={{ color: "#999" }}>
                    Henüz üretilmedi (işletme Masa QR ekranını açmamış)
                  </span>
                )}
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item
                label={
                  <span>
                    Kart Önizleme{" "}
                    <Tooltip title="İşletmenin sadakat kartını müşteri görür gibi açar (damga sıfır; müşteri hesabı/QR taraması gerekmez). Salt görüntüleme.">
                      <InfoCircleOutlined style={{ color: "#999" }} />
                    </Tooltip>
                  </span>
                }>
                {initialValues?.webCode ? (
                  <a
                    href={`https://web.mahalle.app/onizleme/${initialValues.webCode}`}
                    target="_blank"
                    rel="noopener noreferrer">
                    Kartı müşteri gözünden gör ↗
                  </a>
                ) : (
                  <span style={{ color: "#999" }}>
                    Henüz üretilmedi (işletme Masa QR ekranını açmamış)
                  </span>
                )}
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="logo" label="Logo">
                <Upload
                  beforeUpload={() => false}
                  listType="picture"
                  maxCount={1}
                  onChange={(e) => setImageFileList(e.fileList)}
                  accept="image/png, image/jpeg"
                  fileList={imageFileList}>
                  <Button icon={<UploadOutlined />}>Dükkan Logosu Yükle</Button>
                </Upload>
              </Form.Item>
            </Col>
            {initialValues?.imageUrl && (
              <Col span={24}>
                <Form.Item label="Mevcut Logo">
                  <img
                    src={initialValues.imageUrl}
                    alt="Dükkan Logosu"
                    style={{ maxWidth: "200px", marginBottom: "10px" }}
                  />
                </Form.Item>
              </Col>
            )}
          </Row>
        </Form>
      </Card>
      <Card
        type="inner"
        title={
          <Space>
            <EnvironmentOutlined />
            <span>Harita Konumu</span>
            <Tooltip title="Müşteri uygulamasındaki keşif haritasında bu işletmenin görünürlüğünü kontrol eder. Fake/uygunsuz profilin konumunu buradan kaldırın; işletme isterse kendi uygulamasından yeniden konum atayabilir.">
              <InfoCircleOutlined style={{ color: "#999" }} />
            </Tooltip>
          </Space>
        }
        style={{ marginTop: 16 }}
        extra={
          hasLocation && (
            <Button
              danger
              onClick={handleClearLocation}
              loading={isClearLocationLoading}>
              Konumu Kaldır
            </Button>
          )
        }>
        {hasLocation ? (
          <Row gutter={[16, 8]}>
            <Col span={24}>
              <strong>Adres: </strong>
              <span>{shopLocation.formattedAddress || "—"}</span>
            </Col>
            <Col span={12}>
              <strong>Enlem (lat): </strong>
              <span>{shopLocation.latitude ?? "—"}</span>
            </Col>
            <Col span={12}>
              <strong>Boylam (lng): </strong>
              <span>{shopLocation.longitude ?? "—"}</span>
            </Col>
            {shopLocation.googlePlaceId && (
              <Col span={24}>
                <strong>Google Place ID: </strong>
                <span style={{ fontFamily: "monospace", fontSize: 12 }}>
                  {shopLocation.googlePlaceId}
                </span>
              </Col>
            )}
          </Row>
        ) : (
          <span style={{ color: "#999" }}>
            Bu işletmenin atanmış bir harita konumu yok. Keşif haritasında
            görünmez.
          </span>
        )}
      </Card>
    </Spin>
  );
};

export default ShopInformation;
